import axiosInstance from './axiosInstance';

export interface ICheckpointMeta {
  executionType: 'manual' | 'visual' | 'hearing';
  documentApplicable: boolean;
  documentTypes: string[];
  designedParameterAvailable: boolean;
  designedParameterCategory?: string; // UOM Category (e.g., "Length", "Power", "Voltage")
  designedParameterUom: string; // Unit of measurement symbol (e.g., "M", "KW", "V")
  actualParameter?: string; // Actual parameter value/description (required when designedParameterAvailable is true)
  imagesEnabled: boolean;
  remarksEnabled: boolean;
  applicableAuditTypeIds: string[];
}

export interface AmsAuditCheckpoint {
  _id: string;
  amsProjectAuditId: string;
  // API can return amsProjectAssetId as string, object with { id, name, masterAssetId }, or null
  amsProjectAssetId?: string | {
    id?: string;
    _id?: string;
    name: string;
    masterAssetId: string;
  } | null;
  masterAmsCheckpointId: {
    _id: string;
    name: string;
    checkpointType: 'project' | 'asset';
  } | null;
  name: string;
  checkpointType: 'project' | 'asset';
  checkpointMeta: ICheckpointMeta;
  status: 'active' | 'inactive';
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'modified';
  amsProjectAsset?: {
    _id: string;
    name: string;
    masterAssetId: string;
  };
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  updatedBy: {
    _id: string;
    name: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ApplyDefaultCheckpointsRequest {
  amsProjectAuditId: string;
  checkpointType: 'project' | 'asset';
}

export interface ApplyDefaultCheckpointsResponse {
  applied: number;
  skipped: number;
}

export interface CreateAmsAuditCheckpoint {
  amsProjectAuditId: string;
  amsProjectAssetId?: string | null;
  masterAmsCheckpointId: string; // For custom checkpoints, use a placeholder/dummy ID
  name: string;
  checkpointType: 'project' | 'asset';
  checkpointMeta: ICheckpointMeta;
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'modified';
}

export interface UpdateAmsAuditCheckpoint {
  name?: string;
  checkpointMeta?: ICheckpointMeta;
  status?: 'active' | 'inactive';
  approvalStatus?: 'pending' | 'approved' | 'rejected' | 'modified';
  amsProjectAssetId?: string | null;
}

const amsAuditCheckpointService = {
  /**
   * Apply default checkpoints for an audit
   */
  applyDefaultCheckpoints: async (
    data: ApplyDefaultCheckpointsRequest
  ): Promise<ApplyDefaultCheckpointsResponse> => {
    const response = await axiosInstance.post('/ams/ams-audit-checkpoints/apply-default-checkpoints', data);
    return response.data.data;
  },

  /**
   * Get audit checkpoints
   */
  getAuditCheckpoints: async (
    amsProjectAuditId: string,
    checkpointType?: 'project' | 'asset',
    amsProjectAssetId?: string
  ): Promise<AmsAuditCheckpoint[]> => {
    const params: any = { amsProjectAuditId };
    if (checkpointType) {
      params.checkpointType = checkpointType;
    }
    if (amsProjectAssetId) {
      params.amsProjectAssetId = amsProjectAssetId;
    }
    const response = await axiosInstance.get('/ams/ams-audit-checkpoints', { params });
    return response.data.data;
  },

  /**
   * Create a custom audit checkpoint
   */
  createCheckpoint: async (
    data: CreateAmsAuditCheckpoint
  ): Promise<AmsAuditCheckpoint> => {
    const response = await axiosInstance.post('/ams/ams-audit-checkpoints', data);
    return response.data.data;
  },

  /**
   * Update audit checkpoint
   */
  updateCheckpoint: async (
    checkpointId: string,
    data: UpdateAmsAuditCheckpoint
  ): Promise<AmsAuditCheckpoint> => {
    const response = await axiosInstance.patch(`/ams/ams-audit-checkpoints/${checkpointId}`, data);
    return response.data.data;
  },

  /**
   * Delete audit checkpoint
   */
  deleteCheckpoint: async (checkpointId: string): Promise<void> => {
    await axiosInstance.delete(`/ams/ams-audit-checkpoints/${checkpointId}`);
  },
};

export default amsAuditCheckpointService;
